<script setup lang="ts">
import { getMyPosition } from '@/utils/getMyPosition'

import type { FormattedCity } from '@@/server/types/city'
import type { SuggestedOfAddress } from '@@/server/types/geo'

type Props = {
  modelValue?: string
  isInputFocused: boolean
  formattedCitiesList: FormattedCity[]
}

type Emits = {
  (fn: 'update:model-value', value: string): void
  (fn: 'changeFocus', state: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
})

const emit = defineEmits<Emits>()

const { isMobile } = useResponsive()

const geoData = ref<null | SuggestedOfAddress>(null)

const isLoading = ref(true)

const searchValue = computed(() => {
  if (props.isInputFocused || props.modelValue || !geoData.value) {
    return props.modelValue
  }

  for (const city of props.formattedCitiesList) {
    const cityName = city.visible_name.toLowerCase()
    // Ищем соответсвие между городом из списка и гором клиента
    if (isGeoEqualCurrentCityName(cityName)) {
      return city.visible_name
    }
  }

  return props.modelValue
})

const cityUrl = computed(() => {
  if (!geoData.value) {
    return ''
  }

  let url: string = ''

  for (const city of props.formattedCitiesList) {
    const cityName = city.visible_name.toLowerCase()

    // Ищем соответсвие между городом из списка и городом клиента
    if (isGeoEqualCurrentCityName(cityName)) {
      url = `https://${city.domain}`
    }
  }

  return url
})

const isShowmenuBtn = computed(() =>
  Boolean(geoData.value && geoData.value.city && cityUrl.value),
)

const isGeoEqualCurrentCityName = (cityName: string) => {
  const city = geoData.value?.city.toLowerCase()
  const locality = geoData.value?.locality.toLowerCase()

  return (
    cityName === city || cityName === locality || locality?.includes(cityName)
  )
}

const setGeolocation = async () => {
  try {
    const { coords } = await getMyPosition()
    const { latitude, longitude } = coords

    geoData.value = await $fetch('/api/geo/convert-geo-point-to-address-v1', {
      method: 'POST',
      body: {
        geo_point: { latitude, longitude },
        radius_meters: 50,
      },
    })
  } catch (error) {
    console.log('setGeolocation. Ошибка при определении геопозиции', error)
  }
}

const emitSearchQuery = (value: string | undefined) => {
  emit('update:model-value', value ?? '')
}

const changeFocusCityInput = (isFocused: boolean) => {
  emit('changeFocus', isFocused)
}

const onClickOtherCity = () => {
  const searchInput = document.querySelector('#searchInput input')

  if (searchInput instanceof HTMLElement) {
    searchInput.focus()
    changeFocusCityInput(true)
  }
}

onMounted(() => {
  setGeolocation()

  isLoading.value = false
})
</script>

<template>
  <div class="select-city">
    <h2 class="select-city__title">Перейти на сайт города</h2>

    <div
      id="searchInput"
      class="select-city__search-city"
      @click="changeFocusCityInput(true)"
    >
      <the-input
        :model-value="searchValue"
        class="select-city__search-input"
        placeholder="Введите название города"
        aria-disabled="true"
        hide-details
        name="searchInput"
        :clearable="false"
        @update:model-value="emitSearchQuery"
      />
    </div>

    <div class="select-city__btns">
      <div v-if="isShowmenuBtn" class="select-city__menu-btn">
        <the-button :href="cityUrl" block target="_blank">
          Перейти к меню

          <the-icon name="arrow-right" size="32" color="currentColor" />
        </the-button>
      </div>

      <div id="btnOtherCity" class="select-city__other-city-btn">
        <the-button
          :variant="isMobile ? 'secondary' : 'tertiary'"
          :loading="isLoading"
          block
          @click="onClickOtherCity"
        >
          Другой город
        </the-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select-city {
  margin-bottom: 20px;

  @include media($bp-tablet) {
    margin-bottom: 80px;
  }

  @include media($bp-desktop) {
    margin-bottom: 0;
  }

  &__title {
    height: 28px;
    margin-bottom: 30px;
    font-size: 24px;
    font-weight: 800;
    line-height: 28px;
    color: var(--ui-color-white);
    transition: 0.3s;

    @include media($bp-tablet) {
      max-width: 478px;
      height: 94px;
      font-size: 40px;
      line-height: 47px;
    }
  }

  &__search-city {
    margin-bottom: 40px;
  }

  &__search-input {
    width: 100%;

    @include media($bp-tablet) {
      max-width: 501px;
    }

    :deep(.app-input__control) {
      padding: 0;
      background-color: transparent;
      border: none;
      border-bottom: 4px solid var(--ui-color-primary);
      border-radius: unset;

      &:hover {
        background-color: transparent;
      }
    }

    :deep(.app-input__field) {
      font-size: 24px;
      font-weight: 400;
      line-height: unset;
      color: var(--ui-color-white);

      @include media($bp-tablet) {
        font-size: 26px;
      }

      &::placeholder {
        font-size: inherit;
        line-height: unset;
        color: var(--ui-color-sandy-200);
      }
    }
  }

  &__btns {
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: max-content;
    max-height: 120px;
    transition: 0.3s;

    @include media($bp-tablet) {
      flex-direction: row;
      gap: 30px;
      height: 50px;
      max-height: unset;
    }
  }

  &__menu-btn {
    width: 100%;

    @include media($bp-tablet) {
      width: 310px;
    }
  }

  &__other-city-btn {
    width: 100%;

    @include media($bp-tablet) {
      width: max-content;
    }
  }
}

.cities-block--active-search {
  .select-city {
    width: 100%;
    margin-bottom: 0;

    @include media($bp-tablet) {
      width: max-content;
    }

    &__title {
      height: 0;
      margin-bottom: 0;
      overflow: hidden;
      opacity: 0;
    }

    &__btns {
      max-height: 0;
      overflow: hidden;
      opacity: 0;
    }

    &__search-city {
      margin-bottom: 20px;

      @include media($bp-tablet) {
        margin-bottom: 40px;
      }
    }
  }
}
</style>
